import type { ButtonHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '../lib/cn'

type Aparencia = 'primario' | 'secundario' | 'perigo' | 'texto' | 'laranja'

type Tamanho = 'pequeno' | 'medio'

/**
 * O laranja institucional (#f85808, o 500) NÃO aparece aqui como fundo de botão, e não é
 * descuido: branco sobre ele dá ~3,4:1, abaixo dos 4,5:1 que a WCAG AA pede para texto
 * normal. O 600 também reprova, por pouco (~4,3:1). O 700 passa com folga (~5,7:1) e
 * continua sendo laranja. Onde o tom vivo é bem-vindo é em preenchimento sem texto em cima:
 * faixa, barra de destaque, ícone grande.
 */
const APARENCIAS: Record<Aparencia, string> = {
  primario:
    'bg-niteroi-700 text-white shadow-sm hover:bg-niteroi-800 active:bg-niteroi-900 ' +
    'disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none',
  secundario:
    'border border-slate-300 bg-white text-slate-800 shadow-sm hover:border-slate-400 hover:bg-slate-50 ' +
    'active:bg-slate-100 disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none',
  laranja:
    'bg-laranja-700 text-white shadow-sm hover:bg-laranja-800 active:bg-laranja-900 ' +
    'disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none',
  perigo:
    'border border-red-300 bg-white text-red-800 hover:bg-red-50 active:bg-red-100 ' +
    'disabled:border-slate-200 disabled:text-slate-400',
  texto: 'text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900 disabled:text-slate-400',
}

const TAMANHOS: Record<Tamanho, string> = {
  pequeno: 'gap-1.5 px-2.5 py-1.5 text-xs',
  medio: 'gap-2 px-3.5 py-2 text-sm',
}

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  aparencia?: Aparencia
  tamanho?: Tamanho
}

/**
 * Botão único da aplicação.
 *
 * O foco visível é parte do componente, não do tema: metade dos servidores que usam
 * este sistema navega por teclado, e o anel de foco padrão some assim que alguém
 * estiliza um `<button>` sem lembrar dele.
 *
 * `type="button"` por omissão. Dentro de um `<form>`, o padrão do HTML é `submit`, e um
 * botão de "adicionar linha" que envia o formulário inteiro é um defeito que só aparece
 * quando alguém já perdeu o que digitou.
 *
 * @param props atributos de botão, mais a aparência e o tamanho
 * @returns o botão
 */
export function Botao({
  aparencia = 'secundario',
  tamanho = 'medio',
  className,
  type = 'button',
  ...resto
}: Props) {
  return <button type={type} className={cn(classesDeBotao(aparencia, tamanho), className)} {...resto} />
}

/**
 * A aparência do botão, para o que não é `<button>`.
 *
 * @param aparencia a cor e o peso
 * @param tamanho a altura e o corpo do texto
 * @returns as classes
 */
function classesDeBotao(aparencia: Aparencia = 'secundario', tamanho: Tamanho = 'medio'): string {
  return cn(
    'inline-flex items-center justify-center rounded-lg font-medium',
    'transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-niteroi-600 disabled:cursor-not-allowed',
    TAMANHOS[tamanho],
    APARENCIAS[aparencia],
  )
}

/**
 * Um link com cara de botão — para quando o destino é um endereço ("Abrir solicitação").
 *
 * Existe para acabar com `<Link><Botao/></Link>`, que estava em doze telas: botão dentro de link é
 * HTML inválido, o leitor de tela anuncia dois controles onde há um, e o Tab para duas vezes no
 * mesmo lugar. Sendo link, ele ganha de graça o "abrir em nova aba" e o clique do meio.
 *
 * @param props as do `Link` do roteador, mais a aparência e o tamanho
 * @returns o link
 */
export function BotaoLink({
  aparencia = 'secundario',
  tamanho = 'medio',
  className,
  ...resto
}: LinkProps & { aparencia?: Aparencia; tamanho?: Tamanho }) {
  return <Link className={cn(classesDeBotao(aparencia, tamanho), className)} {...resto} />
}
